import { APP_CONFIG } from '@/constants/app';
import {
  VaultSyncConfiguration,
  CreateVaultSyncConfigurationDto,
  UpdateVaultSyncConfigurationDto,
  ManualSyncRequest,
  ManualSyncResult,
  AvailablePeriod,
  TransferToEmployeeRequest,
  BulkTransferToEmployeeRequest,
  TransferResult,
  BulkTransferResult,
} from '@/types/vault-sync-configuration.types';
import { tokenManager } from '@/utils/token-manager';
import axios from 'axios';

// Create axios instance for vault sync configuration service
const vaultSyncApi = axios.create({
  baseURL: APP_CONFIG.api.baseUrl + '/vault-sync',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Setup automatic token refresh interceptors
tokenManager.setupInterceptors(vaultSyncApi);

// API response wrapper type
interface ApiResponse<T> {
  data: T;
  meta?: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

export const vaultSyncConfigurationService = {
  // Get vault sync configuration for a folder
  getConfiguration: async (
    folderUuid: string
  ): Promise<VaultSyncConfiguration | null> => {
    const response = await vaultSyncApi.get<ApiResponse<VaultSyncConfiguration | null>>(
      `/configuration/${folderUuid}`
    );
    return response.data.data;
  },

  // Create or update vault sync configuration
  createOrUpdate: async (
    dto: CreateVaultSyncConfigurationDto
  ): Promise<VaultSyncConfiguration> => {
    const response = await vaultSyncApi.post<ApiResponse<VaultSyncConfiguration>>(
      '/configuration',
      dto
    );
    return response.data.data;
  },

  // Update vault sync configuration
  update: async (
    folderUuid: string,
    dto: UpdateVaultSyncConfigurationDto
  ): Promise<VaultSyncConfiguration> => {
    const response = await vaultSyncApi.put<ApiResponse<VaultSyncConfiguration>>(
      `/configuration/${folderUuid}`,
      dto
    );
    return response.data.data;
  },

  // Delete vault sync configuration
  delete: async (folderUuid: string): Promise<{ message: string }> => {
    const response = await vaultSyncApi.delete<ApiResponse<{ message: string }>>(
      `/configuration/${folderUuid}`
    );
    return response.data.data;
  },

  // Execute manual vault sync
  executeManualSync: async (
    request: ManualSyncRequest
  ): Promise<ManualSyncResult> => {
    const response = await vaultSyncApi.post<ApiResponse<ManualSyncResult>>(
      '/manual',
      request
    );
    return response.data.data;
  },

  // Upload a local file to an employee's vault
  uploadLocalFile: async (data: {
    folderUuid: string;
    employeeUuid: string;
    file: File;
    documentType: string;
    year: number;
    month: number;
    employeeNumber?: string;
  }): Promise<{ success: boolean; documentId: string; vaultId: string }> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('folderUuid', data.folderUuid);
    formData.append('employeeUuid', data.employeeUuid);
    formData.append('documentType', data.documentType);
    formData.append('year', String(data.year));
    formData.append('month', String(data.month));
    if (data.employeeNumber) {
      formData.append('employeeNumber', data.employeeNumber);
    }

    const response = await vaultSyncApi.post<
      ApiResponse<{ success: boolean; documentId: string; vaultId: string }>
    >('/local-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data.data;
  },

  // Get available periods for a folder
  getAvailablePeriods: async (folderUuid: string): Promise<AvailablePeriod[]> => {
    const response = await vaultSyncApi.get<ApiResponse<{ periods: AvailablePeriod[] }>>(
      `/available-periods/${folderUuid}`
    );
    return response.data.data.periods;
  },

  // Transfer a document from employer vault to employee vault(s)
  transferToEmployee: async (
    documentUuid: string,
    request?: TransferToEmployeeRequest,
  ): Promise<TransferResult> => {
    const response = await vaultSyncApi.post<ApiResponse<TransferResult>>(
      `/transfer/${documentUuid}`,
      request || {},
    );
    return response.data.data;
  },

  // Bulk transfer documents from employer vault to employee vaults
  bulkTransferToEmployee: async (
    request: BulkTransferToEmployeeRequest,
  ): Promise<BulkTransferResult> => {
    const response = await vaultSyncApi.post<ApiResponse<BulkTransferResult>>(
      '/bulk-transfer',
      request,
    );
    return response.data.data;
  },
};
